'use client'

import {
  Heart,
  Leaf,
  Quote,
  Sparkles,
} from 'lucide-react'

import {
  BeautyFrame,
  FullBleedImage,
  SafeText,
  beautyInk,
  beautyIvory,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_04.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_05.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_06.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_07.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_08.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/testimonial_09.jpg',
]

const total = 5

const fallbacks = [
  {
    eyebrow: 'Historias reales',
    headline: 'Piel real, confianza real.',
    subheadline: 'Resultados que se sienten. Historias que inspiran.',
    body: 'Una transformación real contada desde la experiencia, la constancia y el cuidado propio.',
    quote: 'Mi piel nunca se había visto tan luminosa y uniforme.',
    author: 'Valeria R.',
    result: 'Resultados visibles en 4 semanas',
    cta: 'Ver rutina',
    testimonial: {
      quote: 'Mi piel nunca se había visto tan luminosa y uniforme.',
      author: 'Valeria R.',
      role: 'Emprendedora',
      result: 'Resultados en 4 semanas',
    },
  },
  {
    eyebrow: 'Testimonio',
    headline: 'Mi piel nunca se había visto tan luminosa y uniforme.',
    subheadline: 'Una experiencia real después de simplificar su rutina.',
    body: 'Resultados en 4 semanas con una rutina más simple, constante y fácil de sostener.',
    quote: 'Mi piel nunca se había visto tan luminosa y uniforme.',
    author: 'Valeria R.',
    result: 'Resultados en 4 semanas',
    testimonial: {
      quote: 'Mi piel nunca se había visto tan luminosa y uniforme.',
      author: 'Valeria R.',
      role: 'Cliente verificada',
      result: 'Resultados en 4 semanas',
    },
  },
  {
    eyebrow: 'Cambio real',
    headline: 'Rutina constante, cambios reales.',
    subheadline: 'Menos brotes, más calma y una textura visiblemente más suave.',
    body: 'La diferencia se notó cuando dejó de saturar su piel y empezó a repetir un ritual claro.',
    result: '+85% notó mejora en su piel',
    proof: 'Autoevaluación de 20 usuarias en 6 semanas.',
    beforeAfter: {
      before: 'Antes',
      after: 'Después',
    },
    stats: [
      {
        value: '+85%',
        label: 'notó mejora en su piel',
        context: 'Autoevaluación de 20 usuarias en 6 semanas.',
      },
    ],
  },
  {
    eyebrow: 'Prueba social',
    headline: 'Ellas ya viven su mejor piel.',
    subheadline: 'Pequeñas historias reales de una rutina que sí se puede sostener.',
    body: 'Resultados reales, ingredientes conscientes y una experiencia más amable con la piel.',
    quote: 'Siento mi piel más fuerte, hidratada y con un glow natural.',
    author: 'Camila G.',
    bullets: [
      'Mi piel se siente más fuerte e hidratada.',
      'Las manchas se han atenuado mucho.',
      'Una rutina simple transformó mi piel.',
    ],
  },
  {
    eyebrow: 'Tu historia',
    headline: 'Tu historia también puede ser la próxima.',
    subheadline: 'Descubre la rutina que transforma tu piel y tu confianza.',
    body: 'Guarda esta historia como recordatorio: la constancia también se nota.',
    result: 'Una rutina clara puede cambiar cómo se ve y cómo se siente tu piel.',
    cta: 'Descubre tu rutina',
  },
]

function getRawSlide(draft: BeautyPackProps['draft'], index: number) {
  const slides = Array.isArray((draft as any)?.slides) ? (draft as any).slides : []
  return slides[index] || {}
}

function normalizeString(value: any) {
  return String(value || '').trim()
}

function normalizeTextItem(item: any) {
  if (typeof item === 'string') return item.trim()

  if (item && typeof item === 'object') {
    return normalizeString(item.title || item.text || item.body || item.label || item.headline)
  }

  return ''
}

function normalizeBullets(rawSlide: any, copy: any, fallback: any) {
  const source = Array.isArray(rawSlide?.bullets) && rawSlide.bullets.length
    ? rawSlide.bullets
    : Array.isArray(copy?.bullets) && copy.bullets.length
      ? copy.bullets
      : Array.isArray(fallback?.bullets)
        ? fallback.bullets
        : []

  return source.map(normalizeTextItem).filter(Boolean)
}

function getTestimonial(rawSlide: any, copy: any, fallback: any) {
  const testimonial = rawSlide?.testimonial || copy?.testimonial || fallback?.testimonial || {}

  return {
    quote: normalizeString(testimonial.quote || rawSlide?.quote || copy?.quote || fallback?.quote),
    author: normalizeString(testimonial.author || rawSlide?.author || copy?.author || fallback?.author),
    role: normalizeString(testimonial.role || rawSlide?.role || fallback?.testimonial?.role),
    result: normalizeString(testimonial.result || rawSlide?.result || copy?.result || fallback?.result),
  }
}

function getBeforeAfter(rawSlide: any, copy: any, fallback: any) {
  return rawSlide?.beforeAfter || copy?.beforeAfter || fallback?.beforeAfter || null
}

function getPrimaryStat(rawSlide: any, copy: any, fallback: any) {
  const rawStats = Array.isArray(rawSlide?.stats) ? rawSlide.stats : []
  const copyStats = Array.isArray(copy?.stats) ? copy.stats : []
  const fallbackStats = Array.isArray(fallback?.stats) ? fallback.stats : []

  if (rawStats.length) return rawStats[0]
  if (copyStats.length) return copyStats[0]
  if (fallbackStats.length) return fallbackStats[0]

  if (rawSlide?.stat || copy?.stat || fallback?.stat) {
    return {
      value: rawSlide?.stat || copy?.stat || fallback?.stat,
      label: rawSlide?.statLabel || copy?.statLabel || fallback?.statLabel,
      context: rawSlide?.proof || copy?.proof || fallback?.proof,
    }
  }

  return null
}

function splitHeadlineForAccent(text: string) {
  const clean = String(text || '').trim()
  const words = clean.split(/\s+/).filter(Boolean)

  if (words.length <= 2) {
    return {
      main: clean,
      accent: '',
    }
  }

  if (words.length <= 5) {
    return {
      main: words.slice(0, -1).join(' '),
      accent: words.slice(-1).join(' '),
    }
  }

  const accentCount = Math.min(3, Math.max(2, Math.floor(words.length * 0.32)))

  return {
    main: words.slice(0, -accentCount).join(' '),
    accent: words.slice(-accentCount).join(' '),
  }
}

function AccentHeadline({
  text,
  accent,
  className = '',
}: {
  text: string
  accent: string
  className?: string
}) {
  const { main, accent: accentText } = splitHeadlineForAccent(text)

  return (
    <SafeText
      className={className}
      style={{ color: beautyInk, fontFamily: beautySerif }}
    >
      {main ? <span>{main}</span> : null}
      {main && accentText ? <br /> : null}
      {accentText ? (
        <span className="italic" style={{ color: accent }}>
          {accentText}
        </span>
      ) : null}
    </SafeText>
  )
}

function Eyebrow({
  children,
  accent,
  light = false,
}: {
  children: string
  accent: string
  light?: boolean
}) {
  return (
    <SafeText
      className="text-[12px] font-bold uppercase tracking-[0.20em]"
      style={{
        color: light ? 'rgba(255,255,255,0.86)' : accent,
        fontFamily: beautySans,
      }}
    >
      {children}
    </SafeText>
  )
}

function FineDivider({
  accent,
  light = false,
  className = '',
}: {
  accent: string
  light?: boolean
  className?: string
}) {
  return (
    <div
      className={`h-px ${className}`}
      style={{ background: light ? 'rgba(255,255,255,0.46)' : `${accent}88` }}
    />
  )
}

function CoverSlide({
  draft,
  copy,
  testimonial,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  testimonial: ReturnType<typeof getTestimonial>
  accent: string
}) {
  return (
    <>
      <FullBleedImage
        {...getBeautyVisual(draft, 0, bg[0], {
          overlay: 'black',
          opacity: 0.18,
        })}
        objectPosition="center"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/28 to-transparent" />

      <div className="absolute bottom-[72px] left-[34px] right-[34px] z-20 text-white">
        <Eyebrow accent={accent} light>
          {copy.eyebrow || 'Historias reales'}
        </Eyebrow>

        <Leaf className="mt-5 h-[34px] w-[34px]" strokeWidth={1.25} style={{ color: '#D9B873' }} />

        <SafeText
          className="mt-7 max-w-[390px] text-[62px] font-normal leading-[0.92] tracking-[-0.058em]"
          style={{ fontFamily: beautySerif }}
        >
          {copy.headline || testimonial.quote || 'Piel real, confianza real.'}
        </SafeText>

        <FineDivider accent={accent} light className="mt-6 w-[88px]" />

        <SafeText
          className="mt-6 max-w-[320px] text-[18px] leading-[1.42] text-white/88"
          style={{ fontFamily: beautySans }}
        >
          {copy.subheadline || testimonial.result || copy.result}
        </SafeText>
      </div>
    </>
  )
}

function QuoteSlide({
  draft,
  copy,
  testimonial,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  testimonial: ReturnType<typeof getTestimonial>
  accent: string
}) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <div className="absolute right-0 top-0 h-full w-full overflow-hidden">
        <FullBleedImage
          {...getBeautyVisual(draft, 1, bg[1], {
            overlay: 'white',
            opacity: 0.05,
          })}
          objectPosition="62% center"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-[#FFF9F0]/98 via-[#FFF9F0]/82 via-[58%] to-transparent" />

      <div className="absolute left-[34px] top-[46px] z-20">
        <Quote className="h-[44px] w-[44px]" strokeWidth={1.3} style={{ color: '#D4A957' }} />
      </div>

      <div className="absolute left-[34px] top-[132px] z-20 max-w-[292px]">
        <SafeText
          className="text-[42px] font-normal leading-[1.02] tracking-[-0.045em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          “{testimonial.quote || copy.headline}”
        </SafeText>

        <FineDivider accent={accent} className="mt-8 w-[72px]" />

        <SafeText
          className="mt-8 text-[14px] font-semibold leading-[1.35]"
          style={{ color: beautyInk, fontFamily: beautySans }}
        >
          — {testimonial.author || 'Cliente verificada'}
        </SafeText>

        {testimonial.role ? (
          <SafeText
            className="mt-1 text-[13px] leading-[1.35]"
            style={{ color: beautyMuted, fontFamily: beautySans }}
          >
            {testimonial.role}
          </SafeText>
        ) : null}
      </div>

      <div
        className="absolute bottom-[72px] left-[42px] z-20 flex h-[132px] w-[132px] items-center justify-center rounded-full text-center"
        style={{ background: '#F2E2C6' }}
      >
        <SafeText
          className="max-w-[100px] text-[16px] leading-[1.35]"
          style={{ color: beautyInk, fontFamily: beautySans }}
        >
          {testimonial.result || copy.result || 'Resultado real'}
        </SafeText>
      </div>
    </div>
  )
}

function TransformationProofSlide({
  draft,
  copy,
  stat,
  beforeAfter,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  stat: any
  beforeAfter: any
  accent: string
}) {
  const mainVisual = getBeautyVisual(draft, 2, bg[2], {
    overlay: 'white',
    opacity: 0.05,
  })

  const compareA = getBeautyVisual(draft, 2, bg[3], {
    overlay: 'white',
    opacity: 0.04,
  })

  const compareB = getBeautyVisual(draft, 2, bg[4], {
    overlay: 'white',
    opacity: 0.04,
  })

  const statValue = stat?.value || copy.stat || '+85%'
  const statLabel = stat?.label || copy.statLabel || 'notó mejora en su piel*'

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ background: beautyIvory }}
    >
      <div className="absolute inset-0 h-full w-full">
        <FullBleedImage {...mainVisual} objectPosition="center" />
      </div>

      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            'linear-gradient(90deg, rgba(244,233,215,0.8) 0%, rgba(244,233,215,0.58) 36%, rgba(244,233,215,0) 72%)',
        }}
      />

      <div className="absolute inset-[10px] z-20 rounded-[22px] border border-[rgba(177,138,74,0.22)]" />

      <div className="absolute left-[34px] top-[34px] z-30 max-w-[230px]">
        <AccentHeadline
          text={copy.headline || 'Rutina constante, cambios reales.'}
          accent={accent}
          className="text-[42px] font-normal leading-[1.02] tracking-[-0.045em]"
        />

        <div
          className="mt-5 h-[2px] w-[38px]"
          style={{ background: `${accent}AA` }}
        />

        <SafeText
          className="mt-6 max-w-[188px] text-[16px] leading-[1.5]"
          style={{ color: beautyMuted, fontFamily: beautySans }}
        >
          {copy.body ||
            copy.subheadline ||
            'Menos brotes, más calma y una textura visiblemente más suave.'}
        </SafeText>
      </div>

      <div
        className="absolute right-[24px] top-[310px] z-40 flex h-[108px] w-[108px] flex-col items-center justify-center rounded-full text-center"
        style={{
          background: '#F4E9D7',
          color: beautyInk,
          boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
        }}
      >
        <div
          className="text-[30px] leading-none"
          style={{ color: accent, fontFamily: beautySerif }}
        >
          {statValue}
        </div>

        <div
          className="mt-1 max-w-[76px] text-[11px] font-medium leading-[1.18]"
          style={{ color: beautyMuted, fontFamily: beautySans }}
        >
          {statLabel}
        </div>
      </div>

      <div className="absolute bottom-[60px] left-[28px] right-[28px] z-30 grid grid-cols-2 gap-[8px]">
        <div className="overflow-hidden rounded-[14px]">
          <div className="relative h-[250px] w-full">
            <FullBleedImage {...compareA} objectPosition="center" />
          </div>

          <div
            className="flex h-[34px] items-center justify-center text-[12px] font-bold uppercase tracking-[0.14em]"
            style={{
              background: '#B79256',
              color: '#fff',
              fontFamily: beautySans,
            }}
          >
            {beforeAfter?.before || 'Antes'}
          </div>
        </div>

        <div className="overflow-hidden rounded-[14px]">
          <div className="relative h-[250px] w-full">
            <FullBleedImage {...compareB} objectPosition="center" />
          </div>

          <div
            className="flex h-[34px] items-center justify-center text-[12px] font-bold uppercase tracking-[0.14em]"
            style={{
              background: '#B79256',
              color: '#fff',
              fontFamily: beautySans,
            }}
          >
            {beforeAfter?.after || 'Después'}
          </div>
        </div>
      </div>

      <SafeText
        className="absolute bottom-[20px] left-0 right-0 z-30 text-center text-[14px] leading-[1.35]"
        style={{ color: beautyMuted, fontFamily: beautySans }}
      >
        {stat?.context || copy.proof || '*Autoevaluación de 20 usuarias en 6 semanas.'}
      </SafeText>
    </div>
  )
}

function SocialProofSlide({
  draft,
  copy,
  bullets,
  testimonial,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  bullets: string[]
  testimonial: ReturnType<typeof getTestimonial>
  accent: string
}) {
  const cards = bullets.length
    ? bullets.slice(0, 3)
    : [
        testimonial.quote || 'Siento mi piel más fuerte, hidratada y con un glow natural.',
        'Las manchas se han atenuado mucho, estoy feliz con los resultados.',
        'Una rutina simple transformó por completo mi piel.',
      ]

  const authors = ['Camila G.', 'Andrea L.', 'Sofía M.']

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <div className="absolute left-[30px] right-[30px] top-[34px] z-20 text-center">
        <SafeText
          className="text-[30px] font-normal leading-[1.05] tracking-[-0.035em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {copy.headline || 'Ellas ya viven su mejor piel'}
        </SafeText>

        <div className="mx-auto mt-5 flex max-w-[250px] items-center gap-3">
          <div className="h-px flex-1" style={{ background: `${accent}66` }} />
          <Heart
            className="h-5 w-5"
            fill={accent}
            strokeWidth={0}
            style={{ color: accent }}
          />
          <div className="h-px flex-1" style={{ background: `${accent}66` }} />
        </div>
      </div>

      <div className="absolute left-[28px] right-[28px] top-[126px] z-20 grid grid-cols-2 gap-[10px]">
        <div className="relative h-[160px] overflow-hidden rounded-[12px]">
          <FullBleedImage
            {...getBeautyVisual(draft, 3, bg[5], {
              overlay: 'white',
              opacity: 0.03,
            })}
            objectPosition="center"
          />
        </div>

        <div
          className="flex h-[140px] flex-col justify-center rounded-[12px] border px-4"
          style={{
            borderColor: 'rgba(33,24,21,0.13)',
            background: 'rgba(255,255,255,0.58)',
          }}
        >
          <SafeText
            className="text-[17px] leading-[1.22]"
            style={{ color: beautyInk, fontFamily: beautySerif }}
          >
            “{cards[0]}”
          </SafeText>

          <SafeText
            className="mt-3 text-[12px] leading-[1.2]"
            style={{ color: beautyMuted, fontFamily: beautySans }}
          >
            — {testimonial.author || authors[0]}
          </SafeText>
        </div>

        <div
          className="flex h-[140px] flex-col justify-center rounded-[12px] border px-4"
          style={{
            borderColor: 'rgba(33,24,21,0.13)',
            background: 'rgba(255,255,255,0.58)',
          }}
        >
          <SafeText
            className="text-[17px] leading-[1.22]"
            style={{ color: beautyInk, fontFamily: beautySerif }}
          >
            “{cards[1] || cards[0]}”
          </SafeText>

          <SafeText
            className="mt-3 text-[12px] leading-[1.2]"
            style={{ color: beautyMuted, fontFamily: beautySans }}
          >
            — {authors[1]}
          </SafeText>
        </div>

        <div className="relative h-[160px] overflow-hidden rounded-[12px]">
          <FullBleedImage
            {...getBeautyVisual(draft, 3, bg[6], {
              overlay: 'white',
              opacity: 0.03,
            })}
            objectPosition="center"
          />
        </div>

        <div className="relative h-[160px] overflow-hidden rounded-[12px]">
          <FullBleedImage
            {...getBeautyVisual(draft, 3, bg[7], {
              overlay: 'white',
              opacity: 0.03,
            })}
            objectPosition="center"
          />
        </div>

        <div
          className="flex h-[140px] flex-col justify-center rounded-[12px] border px-4"
          style={{
            borderColor: 'rgba(33,24,21,0.13)',
            background: 'rgba(255,255,255,0.58)',
          }}
        >
          <SafeText
            className="text-[17px] leading-[1.22]"
            style={{ color: beautyInk, fontFamily: beautySerif }}
          >
            “{cards[2] || cards[0]}”
          </SafeText>

          <SafeText
            className="mt-3 text-[12px] leading-[1.2]"
            style={{ color: beautyMuted, fontFamily: beautySans }}
          >
            — {authors[2]}
          </SafeText>
        </div>
      </div>

      <div
        className="absolute bottom-[24px] left-[28px] right-[28px] z-20 flex items-center justify-center gap-4 rounded-[8px] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.07em]"
        style={{
          color: 'rgba(33,24,21,0.62)',
          background: '#E8DDC9',
          fontFamily: beautySans,
        }}
      >
        <span>Ingredientes conscientes</span>
        <span>•</span>
        <span>Resultados reales</span>
      </div>
    </div>
  )
}

function ClosingSlide({
  draft,
  copy,
  accent,
}: {
  draft: BeautyPackProps['draft']
  copy: ReturnType<typeof getBeautyCopy>
  accent: string
}) {
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: beautyIvory }}>
      <FullBleedImage
        {...getBeautyVisual(draft, 4, bg[8], {
          overlay: 'black',
          opacity: 0.12,
        })}
        objectPosition="center top"
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/22 via-transparent to-[#FFF9F0]/96" />

      <div className="absolute left-[34px] right-[34px] top-[46px] z-20 text-white">
        <SafeText
          className="max-w-[320px] text-[48px] font-normal leading-[1.02] tracking-[-0.045em]"
          style={{ fontFamily: beautySerif }}
        >
          {copy.headline || 'Tu historia también puede ser la próxima.'}
        </SafeText>

        <FineDivider accent={accent} light className="mt-6 w-[80px]" />
      </div>

      <div className="absolute bottom-[42px] left-[34px] right-[34px] z-20">
        <SafeText
          className="max-w-[330px] text-[34px] font-normal leading-[1.12] tracking-[-0.035em]"
          style={{ color: beautyInk, fontFamily: beautySerif }}
        >
          {copy.subheadline || copy.result || copy.body}
        </SafeText>

        <div
          className="mt-7 inline-flex items-center justify-center rounded-[3px] px-[28px] py-[14px] text-[12px] font-bold uppercase tracking-[0.16em] text-white"
          style={{ background: accent, fontFamily: beautySans }}
        >
          {copy.cta || 'Descubre tu rutina'}
        </div>
      </div>
    </div>
  )
}

export default function BeautyTestimonialPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#B18A4A')
  const accent = design.accent || '#B18A4A'

  const rawSlide = getRawSlide(draft, index)
  const copy = getBeautyCopy(draft, index, fallbacks[index])
  const testimonial = getTestimonial(rawSlide, copy, fallbacks[index])
  const beforeAfter = getBeforeAfter(rawSlide, copy, fallbacks[index])
  const bullets = normalizeBullets(rawSlide, copy, fallbacks[index])
  const primaryStat = getPrimaryStat(rawSlide, copy, fallbacks[index])

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 0 || index === 4 ? 'light' : 'dark'}
      className="bg-[#FFF9F0]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <CoverSlide
          draft={draft}
          copy={copy}
          testimonial={testimonial}
          accent={accent}
        />
      ) : null}

      {index === 1 ? (
        <QuoteSlide
          draft={draft}
          copy={copy}
          testimonial={testimonial}
          accent={accent}
        />
      ) : null}

      {index === 2 ? (
        <TransformationProofSlide
          draft={draft}
          copy={copy}
          stat={primaryStat}
          beforeAfter={beforeAfter}
          accent={accent}
        />
      ) : null}

      {index === 3 ? (
        <SocialProofSlide
          draft={draft}
          copy={copy}
          bullets={bullets}
          testimonial={testimonial}
          accent={accent}
        />
      ) : null}

      {index === 4 ? (
        <ClosingSlide
          draft={draft}
          copy={copy}
          accent={accent}
        />
      ) : null}
    </BeautyFrame>
  )
}